import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import "./pricing.scss";

const Pricing = () => {
  const navigate = useNavigate();
  const carouselRef = useRef(null);
  const [selectedPlanIndex, setSelectedPlanIndex] = useState(1);
  const [userPhone, setUserPhone] = useState("");
  const [userEmail, setUserEmail] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  useEffect(() => {
    const phone = localStorage.getItem("phoneNumber");
    const email = localStorage.getItem("userEmail");
    setUserPhone(phone);
    setUserEmail(email);
  }, []);

  // NEW: Detects swipe/scroll position and updates the index/dots
  const handleScroll = () => {
    if (!carouselRef.current) return;
    const scrollLeft = carouselRef.current.scrollLeft;
    const cardWidth = carouselRef.current.offsetWidth * 0.8; // Approximate card width logic
    const newIndex = Math.round(scrollLeft / cardWidth);
    if (newIndex !== selectedPlanIndex && newIndex >= 0 && newIndex < plans.length) {
      setSelectedPlanIndex(newIndex);
    }
  };

  const plans = [
    {
      name: "STARTER",
      credits: "20",
      price: "100",
      color: "#448aff",
      features: ["High Quality Videos", "Commercial use", "No Watermark", "Unlimited validity"],
    },
    {
      name: "PRO",
      credits: "110",
      price: "500",
      color: "#a349ff",
      features: ["10 extra credits", "High Quality Videos", "Commercial use", "No Watermark", "Unlimited validity"],
    },
    {
      name: "ELITE",
      credits: "575",
      price: "2500",
      color: "#ffca28",
      features: ["75 extra credits", "High Quality Videos", "Commercial use", "No Watermark", "Unlimited validity"],
    },
  ];

  const notifyBackend = async (paymentId) => {
    setIsProcessing(true);
    try {
      const response = await fetch("https://api.zulluai.com/api/update-credits", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          phoneNumber: userPhone,
          email: userEmail,
          creditsPurchased: parseInt(plans[selectedPlanIndex].credits),
          paymentId: paymentId,
          planName: plans[selectedPlanIndex].name,
          amountPaid: parseInt(plans[selectedPlanIndex].price),
        }),
      });
      if (response.ok) setShowSuccess(true);
      else alert("Sync failed. Keep ID: " + paymentId);
    } catch (e) {
      alert("Network Error during credit update.");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleRazorpay = () => {
    const plan = plans[selectedPlanIndex];
    const options = {
      key: "rzp_live_KEIYX39z4ATJJa",
      amount: plan.price * 100,
      // amount: 1 * 100,
      currency: "INR",
      name: "ZULLU AI",
      description: `Purchase ${plan.credits} Credits`,
      prefill: { contact: userPhone },
      theme: { color: plan.color },
      handler: (res) => notifyBackend(res.razorpay_payment_id),
    };
    const rzp = new window.Razorpay(options);
    rzp.open();
  };

  return (
    <div className="ps-view">
      <header className="ps-header">
        <h1>TOP UP</h1>
      </header>

      <div
        className="ps-carousel"
        ref={carouselRef}
        onScroll={handleScroll}
      >
        {plans.map((plan, i) => (
          <motion.div
            key={i}
            className={`ps-card ${selectedPlanIndex === i ? "active" : ""}`}
            onClick={() => setSelectedPlanIndex(i)}
            animate={{
              scale: selectedPlanIndex === i ? 1 : 0.85,
              opacity: selectedPlanIndex === i ? 1 : 0.5,
            }}
          >
            <span className="ps-name" style={{ color: plan.color }}>
              {plan.name}
            </span>
            <div className="ps-credits">
              <h2 style={{ backgroundImage: `linear-gradient(to bottom, #fff, ${plan.color})` }}>
                {plan.credits}
              </h2>
              <p>CREDITS</p>
            </div>
            <div className="ps-line"></div>
            <ul className="ps-features">
              {plan.features.map((f, j) => (
                <li key={j}>
                  <i className="fi fi-rr-auto-awesome" style={{ color: plan.color }}></i> {f}
                </li>
              ))}
            </ul>
            <div className="ps-price">₹{plan.price}</div>
          </motion.div>
        ))}
      </div>

      <div className="ps-dots">
        {plans.map((_, i) => (
          <div
            key={i}
            className={`ps-dot ${selectedPlanIndex === i ? "on" : ""}`}
            style={{ background: selectedPlanIndex === i ? plans[i].color : "#333" }}
          ></div>
        ))}
      </div>

      <footer className="ps-footer">
        <button
          className="ps-btn"
          style={{ backgroundColor: plans[selectedPlanIndex].color }}
          onClick={handleRazorpay}
        >
          CONTINUE
        </button>
      </footer>

      <AnimatePresence>
        {showSuccess && (
          <div className="ps-modal-overlay">
            <motion.div
              className="ps-success-card"
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
            >
              <div className="ps-confetti-circle">
                <div className="ps-check-ring"><i className="fi fi-rr-check"></i></div>
              </div>
              <div className="ps-success-body">
                <span className="ps-status-tag">TRANSACTION SECURED</span>
                <h2>{plans[selectedPlanIndex].credits} Credits Added</h2>
                <p>Your neural engine is fueled and ready for generation.</p>
                <div className="ps-perk-grid">
                  <div className="ps-perk"><i className="fi fi-rr-video-camera"></i> No Watermark</div>
                  <div className="ps-perk"><i className="fi fi-rr-copyright"></i> Commercial Use</div>
                  <div className="ps-perk"><i className="fi fi-rr-infinite"></i> No Expiry</div>
                </div>
                <button className="ps-start-gen-btn" onClick={() => navigate("/create")}>
                  START CREATING <i className="fi fi-rr-arrow-right"></i>
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {isProcessing && (
        <div className="ps-loader">
          <div className="spin"></div>
          <p>Syncing Credits...</p>
        </div>
      )}
    </div>
  );
};

export default Pricing;